"use client"

import { useState, useCallback } from "react"
import { pmt, pv, nper, PaymentDueTime } from "financial"

type SolveFor = "loanAmount" | "loanTerm" | "repaymentAmount"

interface CalculatorInputs {
  loanAmount: number
  interestRate: number
  loanTerm: number
  paymentTiming: "arrears" | "advance"
  paymentFrequency: "weekly" | "fortnightly" | "monthly"
  balloonPayment: number
  monthlyFee: number
  repaymentAmount: number
  solveFor: SolveFor
}

interface CalculatorResults {
  interestAndFeesPaid: number
  principalRepaid: number
  totalAmountPaid: number
}

const defaultInputs: CalculatorInputs = {
  loanAmount: 500000,
  interestRate: 6.0,
  loanTerm: 30,
  paymentTiming: "arrears",
  paymentFrequency: "monthly",
  balloonPayment: 0,
  monthlyFee: 0,
  repaymentAmount: 0,
  solveFor: "repaymentAmount",
}

export function solveLoanAmount(inputs: {
  repaymentAmount: number
  loanTerm: number
  periodicRate: number
  paymentsPerYear: number
  balloonPayment: number
  paymentDue: PaymentDueTime
}): number {
  const { repaymentAmount, loanTerm, periodicRate, paymentsPerYear, balloonPayment, paymentDue } = inputs
  const totalPeriodicPayments = loanTerm * paymentsPerYear

  if (balloonPayment > 0) {
    const presentValueBalloon = pv(periodicRate, totalPeriodicPayments, 0, -balloonPayment, paymentDue)
    const adjustedLoanAmount = -pv(periodicRate, totalPeriodicPayments, repaymentAmount, 0, paymentDue)
    return adjustedLoanAmount + presentValueBalloon
  } else {
    return -pv(periodicRate, totalPeriodicPayments, repaymentAmount, 0, paymentDue)
  }
}

export function solveLoanTerm(inputs: {
  repaymentAmount: number
  loanAmount: number
  periodicRate: number
  paymentsPerYear: number
  balloonPayment: number
  paymentDue: PaymentDueTime
}): number {
  const { repaymentAmount, loanAmount, periodicRate, paymentsPerYear, balloonPayment, paymentDue } = inputs

  let effectiveLoanAmount = loanAmount
  if (balloonPayment > 0) {
    // Adjust for balloon payment - this is approximate
    effectiveLoanAmount = loanAmount - balloonPayment * 0.1 // Simple approximation
  }
  const totalPeriodicPayments = nper(periodicRate, -repaymentAmount, effectiveLoanAmount, 0, paymentDue)
  return totalPeriodicPayments / paymentsPerYear
}

export function solveRepaymentAmount(inputs: {
  loanAmount: number
  loanTerm: number
  periodicRate: number
  paymentsPerYear: number
  balloonPayment: number
  paymentDue: PaymentDueTime
}): number {
  const { loanAmount, loanTerm, periodicRate, paymentsPerYear, balloonPayment, paymentDue } = inputs
  const totalPeriodicPayments = loanTerm * paymentsPerYear

  if (balloonPayment > 0) {
    const presentValueBalloon = pv(periodicRate, totalPeriodicPayments, 0, -balloonPayment, paymentDue)
    const adjustedLoanAmount = loanAmount - presentValueBalloon
    return -pmt(periodicRate, totalPeriodicPayments, adjustedLoanAmount, 0, paymentDue)
  } else {
    return -pmt(periodicRate, totalPeriodicPayments, loanAmount, 0, paymentDue)
  }
}

export default function MortgageCalculator() {
  const [option1, setOption1] = useState<CalculatorInputs>(defaultInputs)
  const [option2, setOption2] = useState<CalculatorInputs>(defaultInputs)

  const calculateResults = (inputs: CalculatorInputs): CalculatorInputs & CalculatorResults => {
    let { loanAmount, loanTerm, repaymentAmount } = inputs

    const { interestRate, paymentFrequency, paymentTiming, balloonPayment, monthlyFee, solveFor } = inputs

    // Calculate payment frequency multiplier
    const paymentsPerYear = paymentFrequency === "weekly" ? 52 : paymentFrequency === "fortnightly" ? 26 : 12
    const periodicRate = interestRate / 100 / paymentsPerYear
    const paymentDue = paymentTiming === "advance" ? PaymentDueTime.Begin : PaymentDueTime.End

    // Solve for the selected field
    if (interestRate > 0) {
      if (solveFor === "loanAmount" && repaymentAmount > 0 && loanTerm > 0) {
        loanAmount = solveLoanAmount({
          repaymentAmount,
          loanTerm,
          periodicRate,
          paymentsPerYear,
          balloonPayment,
          paymentDue,
        })
      } else if (solveFor === "loanTerm" && repaymentAmount > 0 && loanAmount > 0) {
        loanTerm = solveLoanTerm({
          repaymentAmount,
          loanAmount,
          periodicRate,
          paymentsPerYear,
          balloonPayment,
          paymentDue,
        })
      } else if (solveFor === "repaymentAmount" && loanAmount > 0 && loanTerm > 0) {
        repaymentAmount = solveRepaymentAmount({
          loanAmount,
          loanTerm,
          periodicRate,
          paymentsPerYear,
          balloonPayment,
          paymentDue,
        })
      }
    }

    // Validate calculated values
    if (loanAmount <= 0 || interestRate < 0 || loanTerm <= 0) {
      return {
        ...inputs,
        interestAndFeesPaid: 0,
        principalRepaid: 0,
        totalAmountPaid: 0,
      }
    }

    const totalPeriodicPayments = loanTerm * paymentsPerYear
    let calculatedRepayment = repaymentAmount

    // Only recalculate repayment amount if we're explicitly solving for it
    if (solveFor === "repaymentAmount") {
      calculatedRepayment = solveRepaymentAmount({
        loanAmount,
        loanTerm,
        periodicRate,
        paymentsPerYear,
        balloonPayment,
        paymentDue,
      })
    }

    // Calculate total payments
    const totalRegularPayments = calculatedRepayment * totalPeriodicPayments
    const totalFeesOverTerm = monthlyFee * 12 * loanTerm
    const totalAmountPaid = totalRegularPayments + balloonPayment + totalFeesOverTerm

    // Calculate interest paid
    const principalRepaid = loanAmount
    const interestAndFeesPaid = totalAmountPaid - principalRepaid

    // Only override the calculated field, preserve user inputs for others
    const result = {
      ...inputs,
      interestAndFeesPaid: Math.round(interestAndFeesPaid),
      principalRepaid: Math.round(principalRepaid),
      totalAmountPaid: Math.round(totalAmountPaid),
    }

    // Update only the field we're solving for
    if (solveFor === "loanAmount") {
      result.loanAmount = Math.round(loanAmount)
    } else if (solveFor === "loanTerm") {
      result.loanTerm = Math.round(loanTerm * 100) / 100 // Round to 2 decimal places
    } else if (solveFor === "repaymentAmount") {
      result.repaymentAmount = Math.round(calculatedRepayment)
    }

    return result
  }

  const updateOption1 = useCallback((field: keyof CalculatorInputs, value: string | number) => {
    setOption1((prev) => {
      // If changing solveFor, preserve current computed values before switching
      if (field === "solveFor") {
        const currentComputed = calculateResults(prev)
        return {
          ...prev,
          loanAmount: currentComputed.loanAmount,
          loanTerm: currentComputed.loanTerm,
          repaymentAmount: currentComputed.repaymentAmount,
          [field]: value as SolveFor,
        }
      }
      return { ...prev, [field]: typeof value === "string" ? value : Number(value) }
    })
  }, [])

  const updateOption2 = useCallback((field: keyof CalculatorInputs, value: string | number) => {
    setOption2((prev) => {
      // If changing solveFor, preserve current computed values before switching
      if (field === "solveFor") {
        const currentComputed = calculateResults(prev)
        return {
          ...prev,
          loanAmount: currentComputed.loanAmount,
          loanTerm: currentComputed.loanTerm,
          repaymentAmount: currentComputed.repaymentAmount,
          [field]: value as SolveFor,
        }
      }
      return { ...prev, [field]: typeof value === "string" ? value : Number(value) }
    })
  }, [])

  // Computed values for display (with calculated fields)
  const computed1 = calculateResults(option1)
  const computed2 = calculateResults(option2)

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const InputField = useCallback(
    ({
      value,
      onChange,
      placeholder,
      step,
      isRate = false,
      tabIndex,
      readonly = false,
      name,
    }: {
      value: number
      onChange: (value: number) => void
      placeholder?: string
      step?: string
      isRate?: boolean
      tabIndex?: number
      readonly?: boolean
      name?: string
    }) => {
      const formatNumberWithCommas = (num: number): string => {
        if (!num && num !== 0) return ""
        return new Intl.NumberFormat("en-AU").format(num)
      }

      const parseNumberFromString = (str: string): number => {
        // Remove all commas and parse as number
        const cleanStr = str.replace(/,/g, "")
        return Number(cleanStr) || 0
      }

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readonly) return
        const inputValue = e.target.value
        const numericValue = parseNumberFromString(inputValue)
        onChange(numericValue)
      }

      const baseClasses = "w-full rounded border px-3 py-2 focus:outline-none"
      const readonlyClasses = readonly
        ? "bg-interest-light-blue/10 border-interest-light-blue text-interest-blue font-semibold cursor-not-allowed"
        : "border-interest-border focus:border-interest-blue"

      if (isRate) {
        // For interest rates, use standard number input without comma formatting
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => (readonly ? undefined : onChange(parseFloat(e.target.value) || 0))}
            placeholder={placeholder}
            step={step}
            tabIndex={tabIndex}
            readOnly={readonly}
            name={name}
            className={`${baseClasses} ${readonlyClasses}`}
          />
        )
      }

      return (
        <input
          type="text"
          value={value ? formatNumberWithCommas(value) : ""}
          onChange={handleChange}
          placeholder={placeholder}
          tabIndex={tabIndex}
          readOnly={readonly}
          name={name}
          className={`${baseClasses} ${readonlyClasses}`}
        />
      )
    },
    []
  )

  const RadioGroup = useCallback(
    ({
      name,
      value,
      onChange,
      options,
      tabIndex,
    }: {
      name: string
      value: string
      onChange: (value: string) => void
      options: { value: string; label: string }[]
      tabIndex?: number
    }) => (
      <div className="flex gap-4">
        {options.map((option, index) => (
          <label key={option.value} className="flex items-center gap-2">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              tabIndex={index === 0 ? tabIndex : undefined}
              className="text-interest-blue focus:ring-interest-blue"
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    ),
    []
  )

  const SelectField = useCallback(
    ({
      value,
      onChange,
      options,
      tabIndex,
      name,
    }: {
      value: string
      onChange: (value: string) => void
      options: { value: string; label: string }[]
      tabIndex?: number
      name?: string
    }) => (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={tabIndex}
        name={name}
        className="border-interest-border focus:border-interest-blue w-full rounded border px-3 py-2 focus:outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ),
    []
  )

  const ResultsDisplay = ({ results, testId }: { results: CalculatorResults; testId: string }) => (
    <div className={`bg-interest-blue space-y-3 rounded p-4 text-white ${testId}`}>
      <div className="flex justify-between">
        <span className="font-medium">Interest + fees paid:</span>
        <span className="font-bold">{formatCurrency(results.interestAndFeesPaid)}</span>
      </div>
      <div className="flex justify-between">
        <span className="font-medium">Principal repaid:</span>
        <span className="font-bold">{formatCurrency(results.principalRepaid)}</span>
      </div>
      <div className="flex justify-between border-t border-white pt-3">
        <span className="font-medium">Total amount paid:</span>
        <span className="text-lg font-bold">{formatCurrency(results.totalAmountPaid)}</span>
      </div>
    </div>
  )

  return (
    <div className="calculator mortgage-calculator w-full">
      <h2 className="bg-interest-light-blue mb-6 p-3 text-center text-sm font-bold text-white md:mb-0">
        Comprehensive Mortgage Calculator
      </h2>

      <div>
        {/* Options Header Row */}
        <div className="bg-interest-blue mb-4 hidden grid-cols-2 gap-4 p-2 text-lg font-bold text-white md:grid md:grid-cols-3">
          <div className="text-center">Option 1</div>
          <div></div>
          <div className="text-center">Option 2</div>
        </div>

        {/* Solver Selection Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3">
          {/* Option 1 Solver Cards */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-2 text-sm font-medium text-gray-700">Calculate:</div>
            <div className="mb-3 grid grid-cols-3 gap-2">
              {[
                { key: "loanAmount", label: "Loan Amount", icon: "💰" },
                { key: "loanTerm", label: "Loan Term", icon: "📅" },
                { key: "repaymentAmount", label: "Repayments", icon: "💸" },
              ].map((solver) => (
                <button
                  key={solver.key}
                  className={`rounded border-2 p-2 text-center transition-all ${
                    option1.solveFor === solver.key
                      ? "border-interest-blue bg-interest-blue text-white shadow-lg"
                      : "hover:border-interest-light-blue border-gray-200"
                  }`}
                  onClick={() => updateOption1("solveFor", solver.key)}
                >
                  <div className="text-lg">{solver.icon}</div>
                  <div className="text-xs font-medium">{solver.label}</div>
                </button>
              ))}
            </div>
            <div className="text-center text-sm text-gray-600">
              <div>
                {option1.solveFor === "loanAmount" && "How much can I borrow with these repayments?"}
                {option1.solveFor === "loanTerm" && "How long will it take to pay off this loan?"}
                {option1.solveFor === "repaymentAmount" && "What will my regular repayments be?"}
              </div>
            </div>
          </div>

          <div className="hidden md:block"></div>

          {/* Option 2 Solver Cards */}
          <div className="hidden md:block">
            <div className="mb-2 text-sm font-medium text-gray-700">Calculate:</div>
            <div className="mb-3 grid grid-cols-3 gap-2">
              {[
                { key: "loanAmount", label: "Loan Amount", icon: "💰" },
                { key: "loanTerm", label: "Loan Term", icon: "📅" },
                { key: "repaymentAmount", label: "Repayments", icon: "💸" },
              ].map((solver) => (
                <button
                  key={solver.key}
                  className={`rounded border-2 p-2 text-center transition-all ${
                    option2.solveFor === solver.key
                      ? "border-interest-blue bg-interest-blue text-white shadow-lg"
                      : "hover:border-interest-light-blue border-gray-200"
                  }`}
                  onClick={() => updateOption2("solveFor", solver.key)}
                >
                  <div className="text-lg">{solver.icon}</div>
                  <div className="text-xs font-medium">{solver.label}</div>
                </button>
              ))}
            </div>
            <div className="text-center text-sm text-gray-600">
              <div>
                {option2.solveFor === "loanAmount" && "How much can I borrow with these repayments?"}
                {option2.solveFor === "loanTerm" && "How long will it take to pay off this loan?"}
                {option2.solveFor === "repaymentAmount" && "What will my regular repayments be?"}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {/* Loan Amount */}
          <div className="order-2 flex gap-2 md:order-1">
            <div
              className={`relative ${
                option1.solveFor === "loanAmount" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option1.solveFor === "loanAmount" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed1.loanAmount}
                onChange={(value) => updateOption1("loanAmount", value)}
                tabIndex={1}
                readonly={option1.solveFor === "loanAmount"}
                name="loan-amount-1"
              />
            </div>
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForLoanAmount1"
                checked={option1.solveFor === "loanAmount"}
                onChange={() => updateOption1("solveFor", "loanAmount")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
          </div>
          <div className="order-1 flex items-center font-medium md:order-2 md:justify-center">Loan amount ($)</div>
          <div className="order-3 flex hidden gap-2 md:order-3 md:flex">
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForLoanAmount2"
                checked={option2.solveFor === "loanAmount"}
                onChange={() => updateOption2("solveFor", "loanAmount")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
            <div
              className={`relative ${
                option2.solveFor === "loanAmount" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option2.solveFor === "loanAmount" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed2.loanAmount}
                onChange={(value) => updateOption2("loanAmount", value)}
                tabIndex={9}
                readonly={option2.solveFor === "loanAmount"}
                name="loan-amount-2"
              />
            </div>
          </div>

          {/* Interest Rate */}
          <div className="order-4 md:order-4">
            <InputField
              value={computed1.interestRate}
              onChange={(value) => updateOption1("interestRate", value)}
              step="0.01"
              isRate={true}
              tabIndex={2}
              name="interest-rate-1"
            />
          </div>
          <div className="order-3 flex items-center font-medium md:order-5 md:justify-center">
            Interest rate (% p.a.)
          </div>
          <div className="order-6 hidden md:order-6 md:block">
            <InputField
              value={computed2.interestRate}
              onChange={(value) => updateOption2("interestRate", value)}
              step="0.01"
              isRate={true}
              tabIndex={10}
              name="interest-rate-2"
            />
          </div>

          {/* Loan Term */}
          <div className="order-6 flex gap-2 md:order-7">
            <div
              className={`relative ${
                option1.solveFor === "loanTerm" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option1.solveFor === "loanTerm" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed1.loanTerm}
                onChange={(value) => updateOption1("loanTerm", value)}
                tabIndex={3}
                readonly={option1.solveFor === "loanTerm"}
                name="loan-term-1"
              />
            </div>
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForLoanTerm1"
                checked={option1.solveFor === "loanTerm"}
                onChange={() => updateOption1("solveFor", "loanTerm")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
          </div>
          <div className="order-5 flex items-center font-medium md:order-8 md:justify-center">Loan term (yrs)</div>
          <div className="order-9 flex hidden gap-2 md:order-9 md:flex">
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForLoanTerm2"
                checked={option2.solveFor === "loanTerm"}
                onChange={() => updateOption2("solveFor", "loanTerm")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
            <div
              className={`relative ${
                option2.solveFor === "loanTerm" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option2.solveFor === "loanTerm" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed2.loanTerm}
                onChange={(value) => updateOption2("loanTerm", value)}
                tabIndex={11}
                readonly={option2.solveFor === "loanTerm"}
                name="loan-term-2"
              />
            </div>
          </div>

          {/* Payment Timing */}
          <div className="order-8 md:order-10">
            <RadioGroup
              name="payment-timing-1"
              value={option1.paymentTiming}
              onChange={(value) => updateOption1("paymentTiming", value)}
              options={[
                { value: "arrears", label: "Arrears" },
                { value: "advance", label: "Advance" },
              ]}
              tabIndex={4}
            />
          </div>
          <div className="order-7 flex items-center font-medium md:order-11 md:justify-center">Payment timing</div>
          <div className="order-12 hidden md:order-12 md:block">
            <RadioGroup
              name="payment-timing-2"
              value={option2.paymentTiming}
              onChange={(value) => updateOption2("paymentTiming", value)}
              options={[
                { value: "arrears", label: "Arrears" },
                { value: "advance", label: "Advance" },
              ]}
              tabIndex={12}
            />
          </div>

          {/* Payment Frequency */}
          <div className="md:order-13 order-10">
            <SelectField
              value={option1.paymentFrequency}
              onChange={(value) => updateOption1("paymentFrequency", value)}
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "fortnightly", label: "Fortnightly" },
                { value: "monthly", label: "Monthly" },
              ]}
              tabIndex={5}
              name="payment-frequency-1"
            />
          </div>
          <div className="md:order-14 order-9 flex items-center font-medium md:justify-center">Payment frequency</div>
          <div className="order-15 md:order-15 hidden md:block">
            <SelectField
              value={option2.paymentFrequency}
              onChange={(value) => updateOption2("paymentFrequency", value)}
              options={[
                { value: "weekly", label: "Weekly" },
                { value: "fortnightly", label: "Fortnightly" },
                { value: "monthly", label: "Monthly" },
              ]}
              tabIndex={13}
              name="payment-frequency-2"
            />
          </div>

          {/* Balloon Payment */}
          <div className="md:order-16 order-12">
            <InputField
              value={computed1.balloonPayment}
              onChange={(value) => updateOption1("balloonPayment", value)}
              tabIndex={6}
              name="balloon-payment-1"
            />
          </div>
          <div className="md:order-17 order-11 flex items-center font-medium md:justify-center">
            Balloon payment ($)
          </div>
          <div className="order-18 md:order-18 hidden md:block">
            <InputField
              value={computed2.balloonPayment}
              onChange={(value) => updateOption2("balloonPayment", value)}
              tabIndex={14}
              name="balloon-payment-2"
            />
          </div>

          {/* Monthly Fee */}
          <div className="order-14 md:order-19">
            <InputField
              value={computed1.monthlyFee}
              onChange={(value) => updateOption1("monthlyFee", value)}
              tabIndex={7}
              name="monthly-fee-1"
            />
          </div>
          <div className="order-13 md:order-20 flex items-center font-medium md:justify-center">Monthly fee ($)</div>
          <div className="order-21 md:order-21 hidden md:block">
            <InputField
              value={computed2.monthlyFee}
              onChange={(value) => updateOption2("monthlyFee", value)}
              tabIndex={15}
              name="monthly-fee-2"
            />
          </div>

          {/* Repayment Amount */}
          <div className="order-16 md:order-22 flex gap-2">
            <div
              className={`relative ${
                option1.solveFor === "repaymentAmount" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option1.solveFor === "repaymentAmount" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed1.repaymentAmount}
                onChange={(value) => updateOption1("repaymentAmount", value)}
                tabIndex={8}
                readonly={option1.solveFor === "repaymentAmount"}
                name="repayment-amount-1"
              />
            </div>
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForRepaymentAmount1"
                checked={option1.solveFor === "repaymentAmount"}
                onChange={() => updateOption1("solveFor", "repaymentAmount")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
          </div>
          <div className="order-15 md:order-23 flex items-center font-medium md:justify-center">
            Repayment amount ($)
          </div>
          <div className="order-24 md:order-24 flex hidden gap-2 md:flex">
            <label className="flex items-center">
              <input
                type="radio"
                name="solveForRepaymentAmount2"
                checked={option2.solveFor === "repaymentAmount"}
                onChange={() => updateOption2("solveFor", "repaymentAmount")}
                className="text-interest-blue focus:ring-interest-blue accent-interest-blue h-4 w-4"
              />
            </label>
            <div
              className={`relative ${
                option2.solveFor === "repaymentAmount" ? "rounded-lg border-2 border-yellow-400 bg-yellow-50 p-1" : ""
              }`}
            >
              {option2.solveFor === "repaymentAmount" && (
                <div className="absolute -right-2 -top-2 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-black">
                  ✨ CALCULATED
                </div>
              )}
              <InputField
                value={computed2.repaymentAmount}
                onChange={(value) => updateOption2("repaymentAmount", value)}
                tabIndex={16}
                readonly={option2.solveFor === "repaymentAmount"}
                name="repayment-amount-2"
              />
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <ResultsDisplay results={computed1} testId="total-amount-paid-1" />
          </div>
          <div className="hidden md:block">
            <ResultsDisplay results={computed2} testId="total-amount-paid-2" />
          </div>
        </div>
      </div>
    </div>
  )
}
